'use client'

import { useSearchParams } from 'next/navigation'
import { type SyntheticEvent, useState } from 'react'

import { AlertBox } from '@/components/common/AlertBox'
import { ButtonDefault, ButtonText } from '@/components/common/Button'
import { InputText } from '@/components/common/Input'
import { Redirecting } from '@/components/common/Redirecting'
import { useAuthGuard } from '@/hooks/useAuthGuard'
import { useReduxDispatch } from '@/store/hooks'
import { userThunks } from '@/store/user'
import { trackEvent } from '@/utils/analyticsUtil'
import { isNonEmptyString, parseError } from '@/utils/commonUtil'

import styles from './SignInForm.module.scss'

const formatError = (error: string) => {
  switch (error) {
    case 'Error (auth/invalid-email).': {
      return 'Invalid email.'
    }
    case 'Error (auth/user-not-found).': {
      return 'User not found.'
    }
    case 'Error (auth/wrong-password).': {
      return 'Wrong Password.'
    }
    case 'Error (auth/too-many-requests).': {
      return 'Too many requests.'
    }
  }
  return error
}

export const SignInForm = () => {
  const authState = useAuthGuard({ statusRequired: 'signedOut' })
  const dispatch = useReduxDispatch()
  const searchParams = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const trackLogin = () => {
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCampaign = searchParams.get('utm_campaign')

    const utmString = [utmSource, utmMedium, utmCampaign].filter(isNonEmptyString).join('|')
    trackEvent({ action: 'account_login', category: 'account', label: utmString || 'direct' })
  }

  const onSubmit = (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault()
    setLoading(true)
    void (async () => {
      try {
        await dispatch(userThunks.signIn({ email, password })).unwrap()
        trackLogin()
      } catch (e) {
        setError(formatError(parseError(e, 'Sign in error')))
        setLoading(false)
      }
    })()
  }

  if (authState === 'denied') return <Redirecting />

  return (
    <div>
      {error && (
        <AlertBox>
          <p>
            <strong>Error:</strong> {error}
          </p>
        </AlertBox>
      )}
      <form onSubmit={onSubmit}>
        <div className={styles.input}>
          <InputText id='email' label='Email' onChange={setEmail} required size='lg' type='email' value={email} />
        </div>
        <div className={styles.input}>
          <InputText
            id='password'
            label='Password'
            onChange={setPassword}
            required
            size='lg'
            type='password'
            value={password}
          />
        </div>
        <div className={styles.actions}>
          <div className={styles.submit}>
            <ButtonDefault disabled={loading} loading={loading} maxwidth size='lg' type='submit'>
              Sign In
            </ButtonDefault>
          </div>
          <div>
            <ButtonText as='LinkRoute' colorScheme='surface' hover='accent' href='/signup' size='lg' textshadow>
              Sign Up
            </ButtonText>
          </div>
          <div>
            <ButtonText
              as='LinkRoute'
              colorScheme='surface'
              hover='accent'
              href='/forgot-password'
              size='lg'
              textshadow
            >
              Forgot your password?
            </ButtonText>
          </div>
        </div>
      </form>
    </div>
  )
}
